import dayjs from "dayjs";
import { Workbook } from "exceljs";

// helper to convert number to Excel column letter (A, B, ..., AA, AB...)
const numberToExcelColumn = (num) => {
  let result = "";
  while (num > 0) {
    let rem = (num - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    num = Math.floor((num - 1) / 26);
  }
  return result;
};

const applyCellStyle = (cell, style = {}) => {
  if (style.font) {
    const fontStyle = { ...style.font };
    if (fontStyle.color) fontStyle.color = { argb: fontStyle.color.replace("#", "") };
    cell.font = fontStyle;
  }
  if (style.fill?.color) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: style.fill.color.replace("#", "") },
    };
  }
  if (style.alignment) {
    cell.alignment = {
      horizontal: style.alignment.horizontal || style.alignment,
      vertical: style.alignment.vertical || "middle",
    };
  }
  if (style.border) {
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  }
};

const renderSummarySection = (worksheet, summaryConfig, startRow, totalColumns) => {
  const endColLetter = numberToExcelColumn(totalColumns);
  let currentRow = startRow;

  summaryConfig.forEach((item) => {
    if (item.type === "title") {
      worksheet.mergeCells(`A${currentRow}:${endColLetter}${currentRow}`);
      const cell = worksheet.getCell(`A${currentRow}`);
      cell.value = item.value;
      applyCellStyle(cell, item.style);
      currentRow++;
    }

    if (item.type === "row") {
      // Label
      const labelCell = worksheet.getCell(`A${currentRow}`);
      labelCell.value = item.label;
      applyCellStyle(labelCell, item.style?.label);

      // Merge label cells if more than 2 cols
      if (totalColumns > 2) {
        const mergeEnd = numberToExcelColumn(totalColumns - 1);
        worksheet.mergeCells(`A${currentRow}:${mergeEnd}${currentRow}`);
      }

      // Value
      const valueCell = worksheet.getCell(`${endColLetter}${currentRow}`);
      if (item.dataType === "number") {
        valueCell.value = Number(item.value);
      } else if (item.dataType === "decimal") {
        valueCell.value = Number(item.value);
        valueCell.numFmt = "#,##0.00";
      } else {
        valueCell.value = item.value;
      }
      applyCellStyle(valueCell, item.style?.value);

      currentRow++;
    }

    if (item.type === "table") {
      // Table header
      const headerRow = worksheet.addRow(item.headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "f5f5f5" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { horizontal: "center" };
      });
      currentRow++;

      // Table data
      item.data.forEach((rowData) => {
        const row = worksheet.addRow(rowData);
        row.eachCell((cell, colIndex) => {
          const colStyle = item.columnStyles?.[colIndex - 1] || {};
          const colType = item.columnTypes?.[colIndex - 1];

          // Apply type formatting
          if (colType === "number") {
            cell.value = Number(cell.value);
          } else if (colType === "decimal") {
            cell.value = Number(cell.value);
            cell.numFmt = "#,##0.00";
          }

          if (colStyle.color) {
            cell.font = { color: { argb: colStyle.color } };
          }
          if (colStyle.alignment) {
            cell.alignment = {
              horizontal: colStyle.alignment,
              vertical: "middle",
            };
          }
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
        currentRow++;
      });
    }
  });

  return currentRow;
};

export const generateAndDownloadExcelFile = async (data, columnDetails, title, summaryConfig = {}, totalColumns = 0) => {
  const formattedTitle = title.toLowerCase().replace(/\s+/g, "_");
  const fileName =
    formattedTitle + "_" + dayjs().format("DD_MM_YYYY_HH_mm_ss");

  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  // Title row
  worksheet.addRow([title]);
  const columnLetter = numberToExcelColumn(columnDetails.length);
  worksheet.mergeCells(`A1:${columnLetter}1`);

  // Headers
  const headersArray = columnDetails.map((column) => column.label);
  const headerRow = worksheet.addRow(headersArray);

  // Column widths
  worksheet.columns = columnDetails.map((col) => ({ width: Number(col.width) }));

  // Style header
  headerRow.font = { bold: true, name: "sans-serif" };
  for (let i = 1; i <= headersArray.length; i++) {
    headerRow.getCell(i).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "dddddd" },
    };
    headerRow.getCell(i).border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  }

  // Data rows
  let y = 0;
  data.forEach((each_data) => {
    const rowColor = y % 2 === 0 ? "e0f8f7" : "effbfb";
    y++;

    const dataRow = columnDetails.map((colDef) =>
      (colDef.type === "number" || colDef.type === "decimal") ? Number(each_data?.[colDef.col]) : each_data?.[colDef.col]
    );

    const cellRow = worksheet.addRow(dataRow);

    for (let i = 1; i <= headersArray.length; i++) {
      cellRow.getCell(i).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: rowColor },
      };
      cellRow.getCell(i).border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    }

    // apply column-specific formatting
    columnDetails.forEach((colDef, idx) => {
      const cell = cellRow.getCell(idx + 1);

      // hyperlink support
      if (colDef.type === "link" && each_data?.[colDef.col]) {
        cell.value = {
          text: each_data[colDef.col], // text to display
          hyperlink: each_data[colDef.linkCol], // actual link
        };
        cell.font = { color: { argb: "0000FF" }, underline: true };
      }

      if (colDef.type === "decimal") {
        cell.numFmt = "#,##0.00";
        cell.font = {
          bold: true,
        };
      }
      if (colDef.color) {
        cell.font = {
          name: "sans-serif",
          color: { argb: colDef.color },
        };
      }
    });
  });

  // Render Summary Section (dynamic)
  if (summaryConfig?.length) {
    const startRow = worksheet.lastRow.number + 2;
    renderSummarySection(worksheet, summaryConfig, startRow, totalColumns);
  }

  // Download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};
